import { useCallback, useEffect, useRef, useState } from 'react';

import { BlinkCounter, readBlinkFrame, warmUpBlinkModel } from './blinkDetector';
import { hasInstantFaceDetector } from './faceDetector';
import { assessFrame, FACE_QUALITY_OK, type FaceQuality, faceQualityFailure } from './faceQuality';

/**
 * How often the preview is assessed, in ms.
 *
 * Fast enough that the ring responds while the applicant is still moving, slow enough that
 * a mid-range phone is not decoding and analysing frames continuously — this runs for as
 * long as the applicant sits on the capture screen, which can be a while.
 */
const SAMPLE_INTERVAL_MS = 250;

/**
 * Sampling interval while counting blinks, in ms.
 *
 * A blink lasts roughly 100-400ms, so the 250ms above would miss most of them outright — the
 * eye can close and reopen entirely between two samples. 60ms catches the shortest blink
 * several times over. It is also real per-frame work on the applicant's phone, which is why
 * only the Liveness step opts in and why it stops the instant the count is reached.
 */
const BLINK_SAMPLE_INTERVAL_MS = 60;

/** Blinks required before the frame is captured automatically. */
export const BLINKS_REQUIRED = 3;

/**
 * How long to wait for those blinks before giving up on them, in ms.
 *
 * Not everybody can blink to command — dry eyes, medication, a neurological condition — and
 * being unable to finish a KYC because you did not blink convincingly is a bad failure that
 * lands hardest on the people least able to work around it. After this the prompt steps
 * aside and the manual Capture button takes over, which is the behaviour the step had before
 * blink capture existed.
 */
const BLINK_TIMEOUT_MS = 15_000;

// Live front-camera capture via getUserMedia — used where a step needs a REAL
// selfie (Face Match source image, Liveness), never a file upload.
//
// With `{ blink: true }` it also counts blinks and captures automatically once there are
// three. Both camera steps opt in: the applicant sees the same circle on both, so making one
// of them behave differently would read as a bug rather than as a distinction. Attach
// `videoRef` to a muted, autoplaying <video>; call `capture()` to snapshot the
// current frame to a JPEG data URL, `retake()` to discard and resume the live
// feed. The camera stream starts when `enabled` and nothing is captured yet, and
// is released on capture, retake, or unmount (so the camera light turns off).
// `error` is set when the camera is unavailable or permission is denied.
export function useCameraCapture(
  enabled: boolean,
  /**
   * `blink: true` turns on the Liveness step's blink-to-capture prompt. OPT-IN, and the
   * default is literally the previous behaviour: Face Match's call site is unchanged, keeps
   * the 224KB detector, and never downloads the 3.7MB landmarker this needs.
   */
  options: { blink?: boolean } = {},
) {
  const blinkMode = options.blink === true;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Live verdict on the preview. Starts OK so the capture button is never disabled before
  // the first sample has run — a control that is dead on arrival reads as a broken screen.
  const [quality, setQuality] = useState<FaceQuality>(FACE_QUALITY_OK);
  // One reused canvas for the whole session. Creating one per sample would allocate a few
  // hundred canvases a minute.
  const sampleCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Blink capture state. `blinks` drives the visible "1 of 3" counter; `blinkTimedOut` says
  // the prompt gave up and manual capture is the way forward.
  const [blinks, setBlinks] = useState(0);
  const [blinkTimedOut, setBlinkTimedOut] = useState(false);
  // Whether the landmarker is actually running. Starts true in blink mode so the prompt does
  // not flicker in before the first frame; flipped off for good if the model cannot load, and
  // the caller then hides the blink prompt rather than asking for something it cannot see.
  const [blinkAvailable, setBlinkAvailable] = useState(blinkMode);
  /**
   * The model has finished loading (or failed) and the gate is live.
   *
   * NOTHING camera-related runs before this: not the stream, not the sampling loop. The
   * camera used to open immediately and warm the model alongside it, which left a window
   * with a live preview that no check was looking at — the one frame that could be captured
   * unjudged — and it made the wait feel longer, because the applicant was staring at a
   * picture of themselves waiting for a button to come alive.
   *
   * It also made the wait genuinely longer: with the loop running, `assessFrame` fell back to
   * its own BlazeFace detector and started downloading THAT as well, so two models competed
   * for the same connection. Holding the camera until this flips means the landmarker gets
   * the bandwidth to itself.
   *
   * Non-blink callers start ready — their path is unchanged in every respect.
   */
  const [blinkReady, setBlinkReady] = useState(!blinkMode);
  // Derived, not stored: "still loading" is exactly "blink mode and not ready yet".
  const blinkWarming = blinkMode && !blinkReady;
  const blinkCounterRef = useRef(new BlinkCounter());

  // Attaching the stream ONLY inside the getUserMedia promise assumed the <video>
  // was already mounted when the camera opened. Face Match broke that assumption:
  // it starts on a guidance screen and renders the <video> only after Continue, so
  // the stream arrived while the element was still null, was never attached, and
  // the effect had no reason to re-run — the applicant reached the capture screen
  // and saw a black circle with the camera light on.
  //
  // A callback ref inverts it: whenever the element appears we attach whatever
  // stream we already hold. Both orderings now work, whichever wins the race.
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (!el) return;
    const stream = streamRef.current;
    if (stream && el.srcObject !== stream) {
      el.srcObject = stream;
      void el.play().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    // Stop streaming once a still is captured, when disabled, or on unmount.
    // `!blinkReady` holds the camera closed until the model is loaded — see blinkReady.
    if (!enabled || !blinkReady || captured !== null) {
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not supported on this device or browser.');
      return;
    }

    let cancelled = false;
    setError(null);

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((stream) => {
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;
        // The element may not exist yet (see attachVideo) — it will pick the
        // stream up on mount. When it does exist, attach right away.
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          void video.play().catch(() => undefined);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to access the camera. Please allow camera access and try again.');
        }
      });

    return () => {
      cancelled = true;
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) track.stop();
        streamRef.current = null;
      }
      // Detach too, or the element keeps showing the last frame of a stopped
      // stream — which looks like a live camera that has frozen.
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [enabled, blinkReady, captured]);

  /**
   * Snapshots the given video element with no further checks.
   *
   * Separate from {@link capture} so the sampling loop can take the frame it has JUST judged.
   * Going through `capture()` would re-read `quality` from a stale closure, and re-deciding a
   * frame that has already passed is how an auto-capture ends up silently doing nothing.
   */
  // `useCallback` with no dependencies, and that is load-bearing rather than habit: the
  // sampling effect below depends on this function, and a fresh identity on every render
  // would tear the loop down and rebuild it each time — resetting the blink counter with it,
  // so the count could never reach three. `setCaptured` is stable, so there is nothing to
  // depend on.
  const captureFrom = useCallback((video: HTMLVideoElement) => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setCaptured(canvas.toDataURL('image/jpeg', 0.9));
  }, []);

  // Start the model download on MOUNT — deliberately not gated on `enabled`.
  //
  // This is where most of the wait went. `enabled` is Face Match's `phase === 'capture'`,
  // which only turns true after the applicant reads the guidance screen and taps Continue,
  // so the download did not begin until the exact moment it was needed and every second of
  // it was dead time. Starting here spends the guidance screen — several seconds the
  // applicant was going to spend anyway — on the download instead.
  //
  // Cheap to be eager: it is memoised, so the second camera step in a run joins the finished
  // promise and is ready instantly, and a step that is never reached has still only fetched
  // a file the browser then caches.
  useEffect(() => {
    if (!blinkMode) return;
    let cancelled = false;
    void warmUpBlinkModel().then((loaded) => {
      if (cancelled) return;
      // Failure opens the camera too, with `blinkAvailable` false: manual capture takes over
      // and the step behaves exactly as it did before blink capture existed. An applicant
      // must never be stranded on "Getting ready…" because a file did not download.
      setBlinkAvailable(loaded);
      setBlinkReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [blinkMode]);

  // Assess the live preview while the camera is open, so the ring and the capture button
  // reflect the CURRENT frame. Stops the moment a still is captured (there is nothing live
  // to judge) or the camera closes, and clears the verdict on the way out so a stale red
  // ring cannot greet the applicant on a retake.
  useEffect(() => {
    // Same `blinkReady` gate as the stream: with no camera open there is nothing to sample,
    // and running the loop early is what used to pull a second detector down the wire.
    if (!enabled || !blinkReady || captured !== null || error) {
      setQuality(FACE_QUALITY_OK);
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    // Blinks only start counting once the face is properly framed, so somebody blinking at
    // the ceiling does not burn the allowance before they are even in shot.
    let deadline: number | null = null;
    blinkCounterRef.current.reset();
    setBlinks(0);
    setBlinkTimedOut(false);

    async function sample() {
      const video = videoRef.current;
      // readyState 2 = HAVE_CURRENT_DATA. Before that there is no frame, and reporting
      // "no face" for a camera that has not started yet would be wrong and alarming.
      if (video && video.readyState >= 2 && video.videoWidth > 0) {
        sampleCanvasRef.current ??= document.createElement('canvas');
        // MediaPipe's VIDEO mode rejects a timestamp that does not advance, so it gets a
        // monotonic clock rather than reading one itself.
        const now = performance.now();

        // In blink mode ONE model pass yields both the blink score and the face box, and the
        // box is handed to assessFrame so the ring is judged from the very frame the blink
        // was read on — rather than loading a second detector beside it.
        const frame = blinkMode ? await readBlinkFrame(video, now) : undefined;
        if (cancelled) return;

        // `undefined` from readBlinkFrame means the landmarker could not run at all — a
        // failed download, an unsupported device. That must NOT be read as "no face": the
        // applicant cannot fix a model that never arrived, and a permanently red ring with a
        // disabled button would end their verification. Passing `undefined` through lets
        // assessFrame fall back to its own detector, so the step behaves exactly as it did
        // before blink capture existed.
        const landmarkerRan = frame !== undefined;
        if (landmarkerRan !== blinkAvailable) setBlinkAvailable(landmarkerRan);

        const knownFaces = landmarkerRan ? (frame?.faces ?? []) : undefined;
        const framingVerdict = await assessFrame(video, sampleCanvasRef.current, now, knownFaces);
        if (cancelled) return;

        // A head turned to the side passes every framing test — it is one face, big enough
        // and centred — but a profile is useless for face matching, and the blink score keeps
        // working on it. So it is checked here, AFTER framing, and only when framing passed:
        // "look straight at the camera" is the wrong thing to say to somebody who is not yet
        // in the circle at all.
        const verdict =
          framingVerdict.ok && frame && !frame.facingCamera
            ? faceQualityFailure('not-frontal')
            : framingVerdict;
        setQuality(verdict);

        if (frame) {
          if (verdict.ok) {
            deadline ??= now + BLINK_TIMEOUT_MS;
            const count = blinkCounterRef.current.push(frame.blinkScore);
            setBlinks(count);
            if (count >= BLINKS_REQUIRED) {
              // Auto-capture. `captureFrom` is used rather than `capture()` so this does not
              // depend on the latest `quality` closure — the verdict for THIS frame is
              // already in hand and it passed.
              captureFrom(video);
              return;
            }
          } else {
            // Lost the face: pause the clock rather than counting it against the applicant.
            deadline = null;
          }

          if (deadline !== null && now > deadline) {
            setBlinkTimedOut(true);
          }
        }
      }

      timer = window.setTimeout(
        () => void sample(),
        blinkMode ? BLINK_SAMPLE_INTERVAL_MS : SAMPLE_INTERVAL_MS,
      );
    }

    void sample();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enabled, blinkReady, captured, error, blinkMode, blinkAvailable, captureFrom]);

  function capture() {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0) {
      return;
    }

    // Refused here as well as being disabled in the UI. The button is the polite guard; this
    // is the one that holds when a keyboard, a double-tap or a future caller gets past it.
    if (!quality.ok) {
      return;
    }

    captureFrom(video);
  }

  function retake() {
    setCaptured(null);
    // The sampling effect resets these when it restarts, but doing it here as well means the
    // counter never shows a stale "3 of 3" for the frame between retake and the next sample.
    blinkCounterRef.current.reset();
    setBlinks(0);
    setBlinkTimedOut(false);
  }

  // `videoRef` is a callback ref, not an object ref — `ref={videoRef}` is unchanged
  // at every call site, but the hook now learns when the element mounts.
  //
  // `quality` is the live verdict on the preview. `quality.faceChecked` says whether the
  // framing check actually ran for that verdict, so a caller can word things honestly when
  // only exposure was measured. `faceDetectionAvailable` reports the no-download case.
  return {
    videoRef: attachVideo,
    captured,
    error,
    capture,
    retake,
    quality,
    faceDetectionAvailable: hasInstantFaceDetector(),
    // Blink capture — meaningful only when `options.blink` was set. `blinkTimedOut` means the
    // prompt gave up and the manual button is the way forward.
    blinks,
    blinkTimedOut,
    blinksRequired: BLINKS_REQUIRED,
    // False once the landmarker is known to be unusable — the caller must stop prompting for
    // blinks it cannot detect and let the manual button carry the step.
    blinkAvailable: blinkMode && blinkAvailable,
    // True only while the model is still loading. The camera is NOT open during this — the
    // caller shows "Getting ready…" in place of the preview and keeps capture disabled.
    blinkWarming,
  };
}
